import axios from "axios";
import React, { createContext, useContext, useEffect, useState, useCallback } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Shared axios for auth-aware requests (cookies + bearer fallback).
const authApi = axios.create({ baseURL: API });

// Optional bearer (used as a fallback when cookies are blocked by the browser).
const BEARER_KEY = "cbb-session-bearer";
export function getStoredBearer() {
  try { return localStorage.getItem(BEARER_KEY) || ""; } catch { return ""; }
}
function setStoredBearer(token) {
  try { token ? localStorage.setItem(BEARER_KEY, token) : localStorage.removeItem(BEARER_KEY); } catch { /* no-op */ }
}

authApi.interceptors.request.use((config) => {
  const b = getStoredBearer();
  if (b) config.headers["Authorization"] = `Bearer ${b}`;
  return config;
});

export { authApi };

// ---- API helpers ----
export async function fetchMe() {
  const res = await authApi.get("/auth/me");
  return res.data.user;
}

export async function exchangeSessionId(session_id) {
  let referrer = "";
  try { referrer = localStorage.getItem("cbb-ref") || ""; } catch { /* no-op */ }
  const res = await authApi.post("/auth/session", { session_id, referrer });
  return res.data; // { ok, user, session_token, is_new }
}

export async function logout() {
  try { await authApi.post("/auth/logout"); } catch { /* ignore */ }
  setStoredBearer("");
}

export async function setUsername(username) {
  const res = await authApi.post("/auth/username", { username });
  return res.data;
}

export async function setMyNation(nation) {
  try { return (await authApi.post("/auth/nation", { nation })).data; } catch { return null; }
}

// Capture ?ref=... from any page load and persist for later signup attribution.
export function captureRef() {
  try {
    const url = new URL(window.location.href);
    const r = url.searchParams.get("ref");
    if (r) localStorage.setItem("cbb-ref", r.slice(0, 64));
  } catch { /* no-op */ }
}

// Build the Emergent Auth redirect URL. Always derived from window.location.origin.
export function buildEmergentLoginUrl(returnTo = "/auth/callback") {
  const base = "https://auth.emergentagent.com/";
  const target = `${window.location.origin}${returnTo}`;
  return `${base}?redirect=${encodeURIComponent(target)}`;
}

export { setStoredBearer };

// ---- Context ----
const AuthCtx = createContext({ user: null, loading: true, refresh: () => {}, signOut: () => {} });

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try { setUser(await fetchMe()); } catch { setUser(null); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const signOut = useCallback(async () => {
    await logout();
    setUser(null);
  }, []);

  return (
    <AuthCtx.Provider value={{ user, loading, refresh, signOut }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() { return useContext(AuthCtx); }

// ---- Archive personal-data API helpers (signed-in) ----
export async function getMyCast() {
  const res = await authApi.get("/me/cast");
  return res.data;
}
export async function getMyArchive() {
  const res = await authApi.get("/me/archive");
  return res.data;
}
export async function getMyStreak() {
  const res = await authApi.get("/me/streak");
  return res.data;
}
export async function getLeaderboard(limit = 25) {
  const res = await authApi.get(`/leaderboard?limit=${limit}`);
  return res.data;
}
export async function getArchiveWall() {
  const res = await authApi.get("/archive/wall");
  return res.data;
}
